import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import clsx from 'clsx';
import { Button } from '@components/common/Button';
import { useSubmitOnboarding } from '@api/hooks/useOnboarding';
import { useGetErpConnection } from '@api/hooks/useErpConnection';
import { ERP_AUTH_PROVIDERS, type ErpAuthProvider } from '@api/quickbooksAuth';
import { useAuthStore } from '@store/authStore';
import {
  WIZARD_STEPS,
  WIZARD_STEP_META,
  FIELDS_BY_DOC_TYPE,
  VENDOR_FIELDS,
  BILL_CREATION_FIELDS,
  type WizardStepId,
  type OnboardingConfig,
} from '@/types/onboarding';
import { WelcomeStep } from './steps/WelcomeStep';
import { PaymentMethodStep } from './steps/PaymentMethodStep';
import { ExtractionFieldsStep } from './steps/ExtractionFieldsStep';
import { ErpIntegrationStep } from './steps/ErpIntegrationStep';
import { MailboxIntegrationStep } from './steps/MailboxIntegrationStep';
import { VendorFieldsStep } from './steps/VendorFieldsStep';
import { BillCreationFieldsStep } from './steps/BillCreationFieldsStep';
import { ReviewStep } from './steps/ReviewStep';

const QB_ONBOARDING_STATE_KEY = 'qb_oauth_onboarding_state';

const isSupportedErpProvider = (erpType?: string | null): erpType is ErpAuthProvider =>
  ERP_AUTH_PROVIDERS.includes(erpType as ErpAuthProvider);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const buildDefaults = (fields: { key: string; isDefault: boolean }[]) =>
  fields.filter((f) => f.isDefault).map((f) => f.key);

const buildDefaultExtractionFields = (): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  for (const [docType, fields] of Object.entries(FIELDS_BY_DOC_TYPE)) {
    result[docType] = buildDefaults(fields);
  }
  return result;
};

interface SavedOnboardingState {
  currentStepIndex?: number;
  extractionFields?: Record<string, string[]>;
  erpConfig?: OnboardingConfig['erp_integration'];
  mailboxConfig?: OnboardingConfig['mailbox_integration'];
  vendorFields?: string[];
  billFields?: string[];
}

const loadSavedOnboardingState = (): SavedOnboardingState | null => {
  const saved = localStorage.getItem(QB_ONBOARDING_STATE_KEY);
  if (!saved) {
    return null;
  }

  try {
    return JSON.parse(saved) as SavedOnboardingState;
  } catch {
    localStorage.removeItem(QB_ONBOARDING_STATE_KEY);
    return null;
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const OnboardingWizard = () => {
  const navigate = useNavigate();
  const submitOnboarding = useSubmitOnboarding();
  const { currentUser } = useAuthStore();
  const { data: erpConnection } = useGetErpConnection(Boolean(currentUser));
  const savedState = loadSavedOnboardingState();

  // --- Step navigation state ---
  const [currentStepIndex, setCurrentStepIndex] = useState(savedState?.currentStepIndex ?? 0);

  // --- Form state ---
  const [extractionFields, setExtractionFields] = useState<Record<string, string[]>>(
    savedState?.extractionFields ?? buildDefaultExtractionFields()
  );
  const [erpConfig, setErpConfig] = useState<OnboardingConfig['erp_integration'] | undefined>(
    savedState?.erpConfig
  );
  const [mailboxConfig, setMailboxConfig] = useState<
    OnboardingConfig['mailbox_integration'] | undefined
  >(savedState?.mailboxConfig);
  const [vendorFields, setVendorFields] = useState<string[]>(() =>
    savedState?.vendorFields ?? buildDefaults(VENDOR_FIELDS)
  );
  const [billFields, setBillFields] = useState<string[]>(() =>
    savedState?.billFields ?? buildDefaults(BILL_CREATION_FIELDS)
  );

  // Clean callback params without relying on them for state restoration.
  useEffect(() => {
    const url = new URL(window.location.href);
    const removedQbParam = url.searchParams.has('qb_connected');
    const removedNetSuiteParam = url.searchParams.has('netsuite_connected');
    const removedXeroParam = url.searchParams.has('xero_connected');
    const removedAjeraParam = url.searchParams.has('ajera_connected');
    const removedMailboxParam = url.searchParams.has('mailbox_connected');
    if (removedQbParam) {
      setErpConfig((prev) => ({ erp_type: prev?.erp_type ?? 'quickbooks', is_connected: true }));
    }
    if (removedNetSuiteParam) {
      setErpConfig((prev) => ({ erp_type: prev?.erp_type ?? 'netsuite', is_connected: true }));
    }
    if (removedXeroParam) {
      setErpConfig((prev) => ({ erp_type: prev?.erp_type ?? 'xero', is_connected: true }));
    }
    if (removedAjeraParam) {
      setErpConfig((prev) => ({ erp_type: prev?.erp_type ?? 'ajera', is_connected: true }));
    }
    url.searchParams.delete('qb_connected');
    url.searchParams.delete('netsuite_connected');
    url.searchParams.delete('xero_connected');
    url.searchParams.delete('ajera_connected');
    url.searchParams.delete('mailbox_connected');

    if (removedQbParam || removedNetSuiteParam || removedXeroParam || removedAjeraParam || removedMailboxParam) {
      const nextUrl = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, '', nextUrl);
    }
  }, []);

  useEffect(() => {
    if (isSupportedErpProvider(erpConnection?.erp_type) && !erpConfig?.is_connected) {
      setErpConfig({ erp_type: erpConnection.erp_type, is_connected: true });
    }
  }, [erpConnection?.erp_type, erpConfig?.is_connected]);

  useEffect(() => {
    localStorage.setItem(
      QB_ONBOARDING_STATE_KEY,
      JSON.stringify({
        currentStepIndex,
        extractionFields,
        erpConfig,
        mailboxConfig,
        vendorFields,
        billFields,
      } satisfies SavedOnboardingState)
    );
  }, [currentStepIndex, extractionFields, erpConfig, mailboxConfig, vendorFields, billFields]);

  // --- Derived values ---
  const currentStep = WIZARD_STEPS[currentStepIndex];
  const currentMeta = WIZARD_STEP_META[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === WIZARD_STEPS.length - 1;

  // --- Navigation helpers ---
  const goToStep = useCallback((stepId: WizardStepId) => {
    const idx = WIZARD_STEPS.indexOf(stepId);
    if (idx !== -1) setCurrentStepIndex(idx);
  }, []);

  const goNext = () => {
    if (!isLastStep) setCurrentStepIndex((i) => i + 1);
  };

  const goBack = () => {
    if (!isFirstStep) setCurrentStepIndex((i) => i - 1);
  };

  // --- Save wizard state to localStorage before QB OAuth redirect ---
  const handleSaveStateForOAuth = useCallback(() => {
    localStorage.setItem(
      QB_ONBOARDING_STATE_KEY,
      JSON.stringify({
        currentStepIndex,
        extractionFields,
        erpConfig,
        mailboxConfig,
        vendorFields,
        billFields,
      })
    );
  }, [currentStepIndex, extractionFields, erpConfig, mailboxConfig, vendorFields, billFields]);

  // --- Submission ---
  const handleComplete = async () => {
    const config: OnboardingConfig = {
      extraction_fields: {
        purchase_order: extractionFields['purchase_order'] ?? [],
        receipt: extractionFields['receipt'] ?? [],
        invoice: extractionFields['invoice'] ?? [],
      },
      erp_integration: erpConfig,
      mailbox_integration: mailboxConfig,
      vendor_fields: vendorFields,
      bill_creation_fields: billFields,
    };

    try {
      await submitOnboarding.mutateAsync(config);
      localStorage.removeItem(QB_ONBOARDING_STATE_KEY);
      navigate('/');
    } catch {
      // Error handled by hook's onError callback
    }
  };

  // --- Step content renderer ---
  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return <WelcomeStep />;
      case 'payment_method':
        return <PaymentMethodStep onSkip={goNext} />;
      case 'extraction_fields':
        return (
          <ExtractionFieldsStep
            extractionFields={extractionFields}
            onChange={setExtractionFields}
          />
        );
      case 'erp_integration':
        return (
          <ErpIntegrationStep
            config={erpConfig}
            onChange={setErpConfig}
            onSaveStateForOAuth={handleSaveStateForOAuth}
          />
        );
      case 'mailbox_integration':
        return (
          <MailboxIntegrationStep
            config={mailboxConfig}
            onChange={setMailboxConfig}
            onSaveStateForOAuth={handleSaveStateForOAuth}
          />
        );
      case 'vendor_fields':
        return <VendorFieldsStep selectedFields={vendorFields} onChange={setVendorFields} />;
      case 'bill_creation_fields':
        return (
          <BillCreationFieldsStep selectedFields={billFields} onChange={setBillFields} />
        );
      case 'review':
        return (
          <ReviewStep
            extractionFields={extractionFields}
            erpConfig={erpConfig}
            mailboxConfig={mailboxConfig}
            vendorFields={vendorFields}
            billFields={billFields}
            onEditStep={goToStep}
          />
        );
      default:
        return null;
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* ---- Step indicator ---- */}
        <nav aria-label="Onboarding progress" className="mb-8">
          <ol className="flex items-center justify-between">
            {WIZARD_STEP_META.map((step, idx) => {
              const isCompleted = idx < currentStepIndex;
              const isCurrent = idx === currentStepIndex;

              return (
                <li key={step.id} className="flex flex-1 items-center last:flex-none">
                  <div className="flex flex-col items-center">
                    {/* Circle */}
                    <div
                      className={clsx(
                        'flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors',
                        isCompleted &&
                          'bg-primary-600 text-white dark:bg-primary-500',
                        isCurrent &&
                          'ring-2 ring-primary-600 dark:ring-primary-400 bg-white dark:bg-gray-900 text-primary-600 dark:text-primary-400',
                        !isCompleted &&
                          !isCurrent &&
                          'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      )}
                    >
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        idx + 1
                      )}
                    </div>

                    {/* Title (hidden on small screens) */}
                    <span
                      className={clsx(
                        'hidden sm:block mt-2 text-xs font-medium text-center max-w-[5rem]',
                        isCurrent
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-gray-500 dark:text-gray-400'
                      )}
                    >
                      {step.title}
                    </span>
                  </div>

                  {/* Connecting line */}
                  {idx < WIZARD_STEP_META.length - 1 && (
                    <div
                      className={clsx(
                        'flex-1 h-0.5 mx-2',
                        idx < currentStepIndex
                          ? 'bg-primary-600 dark:bg-primary-500'
                          : 'bg-gray-200 dark:bg-gray-700'
                      )}
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </nav>

        {/* ---- Step content card ---- */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 md:p-8">
          {renderStep()}
        </div>

        {/* ---- Navigation buttons ---- */}
        <div className="mt-6 flex items-center justify-between">
          {/* Back button */}
          <div>
            {!isFirstStep && (
              <Button variant="secondary" onClick={goBack}>
                <span className="inline-flex items-center gap-1">
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </span>
              </Button>
            )}
          </div>

          {/* Right side: skip + next/complete */}
          <div className="flex items-center gap-3">
            {currentMeta.skippable && (
              <button
                type="button"
                onClick={goNext}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
              >
                Skip for now
              </button>
            )}

            {isLastStep ? (
              <Button
                variant="primary"
                onClick={handleComplete}
                loading={submitOnboarding.isPending}
                disabled={submitOnboarding.isPending}
              >
                Complete Setup
              </Button>
            ) : (
              <Button variant="primary" onClick={goNext}>
                <span className="inline-flex items-center gap-1">
                  Next
                  <ChevronRight className="h-4 w-4" />
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
